import { EventPriority, TrackingEvent, isErrorTrackingEvent, ordByPriorityAndTimestamp } from '../types/tracking-event';
import { TrackingQueueFullError, TrackingStorageError } from '../types/errors';

const DEFAULT_AUTO_SAVE_INTERVAL = 30_000; // 30 seconds

/**
 * EventQueue with local storage persistence
 *
 * Periodically saves events to local storage and restores them on initialization
 */
export class EventQueue {
  private queue: TrackingEvent[] = [];
  private readonly maxSize: number;
  private readonly storageKey: string;
  private autoSaveTimer: number | null = null;

  /**
   * Creates a new EventQueue with local storage persistence
   * @param maxSize Maximum number of events to store in the queue
   * @param storageKey Key to use for local storage
   * @param autoSaveInterval Interval in milliseconds to auto-save to local storage (default: 30 seconds)
   */
  constructor(
    maxSize: number = 1000,
    storageKey: string = 'tracking-event-queue',
    autoSaveInterval: number | null = DEFAULT_AUTO_SAVE_INTERVAL
  ) {
    this.maxSize = maxSize;
    this.storageKey = storageKey;

    // Restore events from local storage
    this.restoreFromStorage();

    // Set up auto-save
    autoSaveInterval && this.startAutoSave(autoSaveInterval);
  }

  /**
   * Starts the auto-save interval
   * @param interval Interval in milliseconds
   */
  private startAutoSave(interval: number): void {
    if (this.autoSaveTimer) {
      clearInterval(this.autoSaveTimer);
    }

    this.autoSaveTimer = window.setInterval(() => {
      this.saveToStorage();
    }, interval);
  }

  /**
   * Stops the auto-save interval
   */
  private stopAutoSave(): void {
    if (this.autoSaveTimer) {
      clearInterval(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }
  }

  /**
   * Saves the current queue to local storage
   */
  private saveToStorage(): void {
    try {
      const serializedEvents = JSON.stringify(this.queue);
      localStorage.setItem(this.storageKey, serializedEvents);
    } catch (error) {
      console.error('Failed to save events to local storage:', error);
      throw new TrackingStorageError(
        'Failed to save events to local storage',
        error instanceof Error ? error : undefined
      );
    }
  }

  /**
   * Restores events from local storage
   */
  private restoreFromStorage(): void {
    try {
      const serializedEvents = localStorage.getItem(this.storageKey);
      if (serializedEvents) {
        const events = JSON.parse(serializedEvents) as TrackingEvent[];

        // Sort events by priority and timestamp
        events.sort(ordByPriorityAndTimestamp);

        // Limit to maxSize
        this.queue = events.slice(0, this.maxSize);

        // Clear storage after successful restore
        localStorage.removeItem(this.storageKey);
      }
    } catch (error) {
      console.error('Failed to restore events from local storage:', error);
      // Don't throw here, just log the error and continue with an empty queue
    }
  }

  private isQueueOverflow(): boolean {
    return this.queue.length > this.maxSize;
  }

  private hasQueueFullEvent(): boolean {
    return !!(
      this.queue.length &&
      isErrorTrackingEvent(this.queue[0]) &&
      this.queue[0].payload.error.message === new TrackingQueueFullError(this.maxSize).message
    );
  }

  /**
   * Adds an event to the queue
   * @param event The event to add
   */
  public enqueue(event: TrackingEvent): void {
    // Insert event in priority order
    const insertIndex = this.queue.findIndex((e) => e.priority > event.priority);
    if (insertIndex === -1) {
      this.queue.push(event);
    } else {
      this.queue.splice(insertIndex, 0, event);
    }

    if (this.isQueueOverflow()) {
      if (!this.hasQueueFullEvent()) {
        this.queue.unshift({
          type: 'Error',
          timestamp: Date.now(),
          priority: EventPriority.ERROR,
          payload: {
            error: new TrackingQueueFullError(this.maxSize),
          },
        });
      }
      this.queue = this.queue.slice(0, this.maxSize);
    }

    // Save to storage after adding an event
    this.saveToStorage();
  }

  /**
   * Removes and returns the next event from the queue
   * @returns The next event or undefined if the queue is empty
   */
  public dequeue(): TrackingEvent | undefined {
    const event = this.queue.shift();
    if (event) {
      // Save to storage after removing an event
      this.saveToStorage();
    }
    return event;
  }

  /**
   * Gets the current size of the queue
   */
  public get size(): number {
    return this.queue.length;
  }

  /**
   * Checks if the queue is empty
   */
  public get isEmpty(): boolean {
    return this.queue.length === 0;
  }

  /**
   * Clears the queue and removes events from local storage
   */
  public clear(): void {
    this.queue = [];
    try {
      localStorage.removeItem(this.storageKey);
    } catch (error) {
      console.error('Failed to clear events from local storage:', error);
    }
  }

  /**
   * Gets a copy of all events in the queue
   */
  public getEvents(): TrackingEvent[] {
    return [...this.queue];
  }

  /**
   * Manually saves the queue to local storage
   */
  public save(): void {
    this.saveToStorage();
  }

  /**
   * Manually restores the queue from local storage
   */
  public restore(): void {
    this.restoreFromStorage();
  }

  /**
   * Sets a new auto-save interval
   * @param interval Interval in milliseconds
   */
  public setAutoSaveInterval(interval: number): void {
    this.startAutoSave(interval);
  }

  /**
   * Stops auto-saving to local storage
   */
  public stopAutoSaving(): void {
    this.stopAutoSave();
  }
}
